/**
 * Constantes de tipos que permite asignar los estados de la aplicación
 *
 * IMPORTANTE: cada valor de string debe ser ÚNICO en todo el archivo.
 *
 * Antes, todos los `.ERROR` valían 'ERROR' (string genérico) y colisionaban
 * entre slices. Cualquier `dispatch({ type: <SLICE>.<OP>.ERROR, ... })`
 * disparaba a la vez `case AUTH.ERROR:` del reducer authenticate (porque
 * AUTH.ERROR también valía 'ERROR'). Resultado: el state.login.type pasaba
 * a 'ERROR', el Router lo evaluaba como "no LOGGEDIN" y mostraba <Login/>
 * — aunque la sesión de Firebase Auth siguiera viva.
 *
 * Era especialmente visible para clientes (queries con permission-denied
 * o errores transitorios) y se manifestaba como "se desloguea al hacer una
 * consulta". El refresh (F5) no rescataba porque el state ya estaba en
 * 'ERROR'; solo Ctrl+Shift+R hacía bootstrap completo y restauraba.
 */

export const AUTH = {
  LOGGINGIN: 'LOGGINGIN',
  LOGGEDIN: 'LOGGEDIN',
  LOGGINGOUT: 'LOGGINGOUT',
  LOGGEDOUT: 'LOGGEDOUT',
  CANCELED: 'CANCELED',
  // Autenticación en 2 pasos:
  //  - MFA_TOTP_REQUIRED: signIn lanzó auth/multi-factor-auth-required
  //    (pre-sesión, el resolver espera el código de la app autenticadora)
  //  - MFA_REQUIRED: sesión del 1er factor activa, falta el OTP por email
  //  - MFA_ENROLL_REQUIRED: el admin exige 2FA y el usuario no tiene método
  MFA_TOTP_REQUIRED: 'AUTH_MFA_TOTP_REQUIRED',
  MFA_REQUIRED: 'AUTH_MFA_REQUIRED',
  MFA_ENROLL_REQUIRED: 'AUTH_MFA_ENROLL_REQUIRED',
  ERROR: 'AUTH_ERROR'
}

export const UPLOAD = {
  UPLOAD: 'UPLOAD',
  UPLOADING: 'UPLOADING',
  UPLOADED: 'UPLOADED',
  ERROR: 'UPLOAD_ERROR'
}

export const ROLES = {
  GET_ROLES: 'GET_ROLES',
  GETTING_ROLES: 'GETTING_ROLES',
  GETTED_ROLES: 'GETTED_ROLES',
  ERROR: 'ROLES_ERROR'
}

export const DOCUMENTS = {

  CREATE: {
    CREATE_DOCUMENTS: 'CREATE_DOCUMENTS',
    CREATING_DOCUMENTS: 'CREATING_DOCUMENTS',
    CREATED_DOCUMENTS: 'CREATED_DOCUMENTS',
    ERROR: 'DOCUMENTS_CREATE_ERROR'
  },

  READ: {
    READ_DOCUMENTS: 'READ_DOCUMENTS',
    READING_DOCUMENTS: 'READING_DOCUMENTS',
    READED_DOCUMENTS: 'READED_DOCUMENTS',
    ERROR: 'DOCUMENTS_READ_ERROR'
  },

  UPDATE: {
    UPDATE_DOCUMENTS: 'UPDATE_DOCUMENTS',
    UPDATING_DOCUMENTS: 'UPDATING_DOCUMENTS',
    UPDATED_DOCUMENTS: 'UPDATED_DOCUMENTS',
    ERROR: 'DOCUMENTS_UPDATE_ERROR'
  },

  DELETE: {
    DELETE_DOCUMENTS: 'DELETE_DOCUMENTS',
    DELETING_DOCUMENTS: 'DELETING_DOCUMENTS',
    DELETED_DOCUMENTS: 'DELETED_DOCUMENTS',
    ERROR: 'DOCUMENTS_DELETE_ERROR'
  },

  CLOSE: {
    CLOSE_DOCUMENTS: 'CLOSE_DOCUMENTS',
    CLOSING_DOCUMENTS: 'CLOSING_DOCUMENTS',
    CLOSED_DOCUMENTS: 'CLOSED_DOCUMENTS',
    ERROR: 'DOCUMENTS_CLOSE_ERROR'
  },

  ACCOUNTING: {
    ACCOUNTING_THROW_BACK: 'ACCOUNTING_THROW_BACK',
    ERROR: 'DOCUMENTS_ACCOUNTING_ERROR'
  },

  CLONE: {
    CLONE_DOCUMENTS: 'CLONE_DOCUMENTS',
    CLONING_DOCUMENTS: 'CLONING_DOCUMENTS',
    CLONED_DOCUMENTS: 'CLONED_DOCUMENTS',
    ERROR: 'DOCUMENTS_CLONE_ERROR'
  },

  SEARCHER: {
    SEARCH_DOCUMENTS: 'SEARCH_DOCUMENTS',
    SEARCHING_DOCUMENTS: 'SEARCHING_DOCUMENTS',
    FOUND_DOCUMENTS: 'FOUND_DOCUMENTS',
    NOT_FOUND_DOCUMENTS: 'NOT_FOUND_DOCUMENTS',
    ERROR: 'DOCUMENTS_SEARCHER_ERROR'
  }

}

export const CUSTOMERS = {

  CREATE: {
    CREATE_CUSTOMERS: 'CREATE_CUSTOMERS',
    CREATING_CUSTOMERS: 'CREATING_CUSTOMERS',
    CREATED_CUSTOMERS: 'CREATED_CUSTOMERS',
    ERROR: 'CUSTOMERS_CREATE_ERROR'
  },

  READ: {
    READ_CUSTOMERS: 'READ_CUSTOMERS',
    READING_CUSTOMERS: 'READING_CUSTOMERS',
    READED_CUSTOMERS: 'READED_CUSTOMERS',
    ERROR: 'CUSTOMERS_READ_ERROR'
  },

  UPDATE: {
    UPDATE_CUSTOMERS: 'UPDATE_CUSTOMERS',
    UPDATING_CUSTOMERS: 'UPDATING_CUSTOMERS',
    UPDATED_CUSTOMERS: 'UPDATED_CUSTOMERS',
    ERROR: 'CUSTOMERS_UPDATE_ERROR'
  },

  DELETE: {
    DELETE_CUSTOMERS: 'DELETE_CUSTOMERS',
    DELETING_CUSTOMERS: 'DELETING_CUSTOMERS',
    DELETED_CUSTOMERS: 'DELETED_CUSTOMERS',
    ERROR: 'CUSTOMERS_DELETE_ERROR'
  }

}

export const USERS = {

  CREATE: {
    CREATE_USERS: 'CREATE_USERS',
    CREATING_USERS: 'CREATING_USERS',
    CREATED_USERS: 'CREATED_USERS',
    ERROR: 'USERS_CREATE_ERROR'
  },

  READ: {
    READ_USERS: 'READ_USERS',
    READING_USERS: 'READING_USERS',
    READED_USERS: 'READED_USERS',
    ERROR: 'USERS_READ_ERROR'
  },

  UPDATE: {
    UPDATE_USERS: 'UPDATE_USERS',
    UPDATING_USERS: 'UPDATING_USERS',
    UPDATED_USERS: 'UPDATED_USERS',
    ERROR: 'USERS_UPDATE_ERROR'
  },

  DELETE: {
    DELETE_USERS: 'DELETE_USERS',
    DELETING_USERS: 'DELETING_USERS',
    DELETED_USERS: 'DELETED_USERS',
    ERROR: 'USERS_DELETE_ERROR'
  }

}

export const REFERENCES = {

  CREATE: {
    CREATE_REFERENCE: 'CREATE_REFERENCE',
    CREATING_REFERENCE: 'CREATING_REFERENCE',
    CREATED_REFERENCE: 'CREATED_REFERENCE',
    ERROR: 'REFERENCES_CREATE_ERROR'
  },

  READ: {
    READ_REFERENCE: 'READ_REFERENCE',
    READING_REFERENCE: 'READING_REFERENCE',
    READED_REFERENCE: 'READED_REFERENCE',
    ERROR: 'REFERENCES_READ_ERROR'
  },

  UPDATE: {
    UPDATE_REFERENCE: 'UPDATE_REFERENCE',
    UPDATING_REFERENCE: 'UPDATING_REFERENCE',
    UPDATED_REFERENCE: 'UPDATED_REFERENCE',
    ERROR: 'REFERENCES_UPDATE_ERROR'
  },

  DELETE: {
    DELETE_REFERENCE: 'DELETE_REFERENCE',
    DELETING_REFERENCE: 'DELETING_REFERENCE',
    DELETED_REFERENCE: 'DELETED_REFERENCE',
    ERROR: 'REFERENCES_DELETE_ERROR'
  }

}

export const TRASH = {

  READ: {
    READ_TRASH: 'READ_TRASH',
    READING_TRASH: 'READING_TRASH',
    READED_TRASH: 'READED_TRASH',
    ERROR: 'TRASH_READ_ERROR'
  },

  RESTORE: {
    RESTORE_TRASH: 'RESTORE_TRASH',
    RESTORING_TRASH: 'RESTORING_TRASH',
    RESTORED_TRASH: 'RESTORED_TRASH',
    ERROR: 'TRASH_RESTORE_ERROR'
  },

  PURGE: {
    PURGE_TRASH: 'PURGE_TRASH',
    PURGING_TRASH: 'PURGING_TRASH',
    PURGED_TRASH: 'PURGED_TRASH',
    ERROR: 'TRASH_PURGE_ERROR'
  },

  PURGE_ALL: {
    PURGE_ALL_TRASH: 'PURGE_ALL_TRASH',
    PURGING_ALL_TRASH: 'PURGING_ALL_TRASH',
    PURGED_ALL_TRASH: 'PURGED_ALL_TRASH',
    ERROR: 'TRASH_PURGE_ALL_ERROR'
  }

}

export const FOLDERS = {

  TEMPLATES: {
    CREATE: {
      CREATE_TEMPLATE: 'CREATE_TEMPLATE',
      CREATING_TEMPLATE: 'CREATING_TEMPLATE',
      CREATED_TEMPLATE: 'CREATED_TEMPLATE',
      ERROR: 'FOLDERS_TEMPLATES_CREATE_ERROR'
    },

    READ: {
      READ_TEMPLATE: 'READ_TEMPLATE',
      READING_TEMPLATE: 'READING_TEMPLATE',
      READED_TEMPLATE: 'READED_TEMPLATE',
      ERROR: 'FOLDERS_TEMPLATES_READ_ERROR'
    },

    UPDATE: {
      UPDATE_TEMPLATE: 'UPDATE_TEMPLATE',
      UPDATING_TEMPLATE: 'UPDATING_TEMPLATE',
      UPDATED_TEMPLATE: 'UPDATED_TEMPLATE',
      ERROR: 'FOLDERS_TEMPLATES_UPDATE_ERROR'
    },

    DELETE: {
      DELETE_TEMPLATE: 'DELETE_TEMPLATE',
      DELETING_TEMPLATE: 'DELETING_TEMPLATE',
      DELETED_TEMPLATE: 'DELETED_TEMPLATE',
      ERROR: 'FOLDERS_TEMPLATES_DELETE_ERROR'
    }
  },

  // Restringir acceso a una carpeta (spec 2026-10-07 §3).
  RESTRICT: {
    RESTRICT_FOLDER: 'RESTRICT_FOLDER',
    RESTRICTING_FOLDER: 'RESTRICTING_FOLDER',
    RESTRICTED_FOLDER: 'RESTRICTED_FOLDER',
    ERROR: 'FOLDERS_RESTRICT_ERROR'
  }

}
